import { motion } from "framer-motion";
import { Instagram, Youtube, ArrowUpRight } from "lucide-react";
import { EASE, SectionTag, MaskedLine, Counter, MagneticButton } from "./Motion";

export default function Founder() {
  return (
    <section id="founder" data-testid="founder-section" className="relative overflow-hidden py-28 sm:py-36">
      <div className="pointer-events-none absolute left-0 top-1/3 h-[420px] w-[420px] rounded-full bg-gold/[0.05] blur-[130px]" />
      <div className="relative mx-auto max-w-7xl px-6 sm:px-8 lg:px-16">
        <SectionTag index="06" label="The Founder" testid="founder-tag" />

        <div className="mt-14 grid items-center gap-14 lg:grid-cols-12 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 48 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1.1, ease: EASE }}
            className="lg:col-span-5"
          >
            <motion.div
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
              className="bg-gradient-to-br from-gold/60 via-white/5 to-gold/20 p-px [clip-path:polygon(0_0,calc(100%-28px)_0,100%_28px,100%_100%,28px_100%,0_calc(100%-28px))]"
            >
              <div className="relative overflow-hidden [clip-path:polygon(0_0,calc(100%-28px)_0,100%_28px,100%_100%,28px_100%,0_calc(100%-28px))]">
                <img
                  src="https://images.unsplash.com/photo-1668605335655-2dc1f8bec86b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxjaW5lbWF0aWMlMjBkYXJrJTIwc3R1ZGlvJTIwcG9ydHJhaXQlMjBjcmVhdG9yJTIwcG9kY2FzdHxlbnwwfHx8fDE3ODkxMTkyNTd8MA&ixlib=rb-4.1.0&q=85"
                  alt="Lakshya Soni — Founder of BEATKINGSMM"
                  loading="lazy"
                  className="aspect-[4/5] w-full object-cover"
                  data-testid="founder-portrait"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5">
                  <div className="font-mono text-[9px] tracking-[0.3em] text-goldlight/80">FOUNDER & CEO</div>
                  <div className="mt-1 font-serif text-xl italic text-neutral-100">Lakshya Soni</div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          <div className="lg:col-span-7">
            <h2 className="text-4xl font-bold tracking-tight text-neutral-50 sm:text-5xl">
              <MaskedLine>BUILT BY A CREATOR.</MaskedLine>
              <MaskedLine delay={0.14} innerClassName="font-serif italic font-medium text-gold-grad">
                For creators.
              </MaskedLine>
            </h2>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.25 }}
            >
              <div
                className="mt-8 font-serif text-5xl italic leading-none text-gold-grad sm:text-7xl"
                data-testid="founder-name"
              >
                Lakshya Soni
              </div>
              <div className="mt-4 font-mono text-[11px] uppercase tracking-[0.35em] text-neutral-400">
                Entrepreneur · Creator · Founder — BEATKINGSMM
              </div>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.35 }}
              className="mt-8 max-w-xl text-base font-light leading-relaxed text-neutral-400 sm:text-lg"
            >
              Before BEATKINGSMM was a platform, it was one creator's answer to his own grind —
              over a million followers built post by post, deal by deal. Lakshya engineered the
              machine he wished existed, then handed the keys to everyone.
            </motion.p>

            <div className="mt-10 grid max-w-lg grid-cols-2 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.8, ease: EASE, delay: 0.45 }}
                className="border-l-2 border-gold/50 pl-5"
              >
                <div className="flex items-center gap-2 text-gold">
                  <Instagram size={15} />
                  <span className="font-mono text-[9px] tracking-[0.25em] text-neutral-500">INSTAGRAM</span>
                </div>
                <div className="mt-2 text-4xl font-bold tracking-tight text-neutral-50 sm:text-5xl">
                  <Counter to={1.1} decimals={1} suffix="M+" duration={2.2} testid="founder-counter-instagram" />
                </div>
                <div className="mt-1 text-xs text-neutral-500">followers, earned one post at a time</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.8, ease: EASE, delay: 0.55 }}
                className="border-l-2 border-gold/50 pl-5"
              >
                <div className="flex items-center gap-2 text-gold">
                  <Youtube size={15} />
                  <span className="font-mono text-[9px] tracking-[0.25em] text-neutral-500">YOUTUBE</span>
                </div>
                <div className="mt-2 text-4xl font-bold tracking-tight text-neutral-50 sm:text-5xl">
                  <Counter to={300} suffix="K+" duration={2.2} testid="founder-counter-youtube" />
                </div>
                <div className="mt-1 text-xs text-neutral-500">subscribers and counting</div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.6 }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.6 }}
              className="mt-12"
            >
              <div className="relative inline-block">
                <p className="text-xl font-extrabold uppercase tracking-tight text-neutral-100 sm:text-2xl" data-testid="founder-statement">
                  He doesn't sell reach. <span className="text-gold-grad">He lives it.</span>
                </p>
                <motion.div
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true, amount: 0.8 }}
                  transition={{ duration: 1.2, ease: EASE, delay: 0.8 }}
                  className="mt-3 h-[3px] w-full origin-left bg-gradient-to-r from-gold to-transparent"
                />
              </div>
              <div className="mt-8">
                <MagneticButton href="https://instagram.com/beatkingsmm.in" variant="ghost" testid="founder-cta">
                  Follow the Journey <ArrowUpRight size={16} />
                </MagneticButton>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
